import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Buyer confirms delivery, then the platform attempts an automatic Stripe
 * Transfer to the seller. Delivery confirmation is committed first; the
 * payout attempt runs after and its failure never rolls back the
 * confirmation. Errors surface to admins (audit + notification) but not the
 * buyer, and the admin "Release payout" button remains available as a retry.
 */
export const confirmDeliveryAndAutoRelease = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ orderId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    // Step 1: commit delivery confirmation as the buyer via existing RPC.
    // The RPC enforces buyer ownership + status transitions and logs a
    // `delivery_confirmed` audit event.
    const { error: confirmErr } = await supabase.rpc(
      "confirm_delivery" as never,
      { _order_id: data.orderId } as never,
    );
    if (confirmErr) throw new Error(confirmErr.message);

    // Step 2: attempt automatic payout. Never let this fail the buyer's
    // confirmation UX; log + notify admins on error.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let payoutTransferId: string | null = null;
    let payoutAttempted = false;
    let payoutError: string | null = null;

    try {
      // Pre-check: skip silently if not eligible (dispute, already released, etc.).
      const { data: order, error: ordErr } = await supabaseAdmin
        .from("orders")
        .select("id,status,has_active_dispute,stripe_transfer_id,payout_released_at,delivery_confirmed_at")
        .eq("id", data.orderId)
        .maybeSingle();
      if (ordErr) throw new Error(ordErr.message);
      if (!order) throw new Error("Order not found after confirmation");

      const eligible =
        order.status === "delivered" &&
        !!order.delivery_confirmed_at &&
        !order.stripe_transfer_id &&
        !order.payout_released_at &&
        !order.has_active_dispute;

      if (!eligible) {
        return { ok: true, payoutAttempted: false, transferId: null };
      }

      payoutAttempted = true;
      await supabaseAdmin.rpc("_log_payment_event" as never, {
        _order_id: data.orderId,
        _event_type: "automatic_payout_attempted",
        _actor_id: userId,
        _amount_pence: null,
        _metadata: { source: "buyer_confirmation" },
      } as never);

      const { performOrderPayout } = await import("./order-payout.server");
      const result = await performOrderPayout(data.orderId, {
        source: "system",
        actorId: userId,
        reference: null,
        notes: "Auto-released after buyer delivery confirmation",
      });
      if (result.blocked) {
        return { ok: true, payoutAttempted: false, transferId: null };
      }
      payoutTransferId = result.transferId;

      await supabaseAdmin.rpc("_log_payment_event" as never, {
        _order_id: data.orderId,
        _event_type: "automatic_payout_succeeded",
        _actor_id: userId,
        _amount_pence: result.amount,
        _metadata: { transfer_id: result.transferId, already_released: !!result.alreadyReleased },
      } as never);

    } catch (err) {
      payoutError = err instanceof Error ? err.message : String(err);
      // Log full error server-side; do NOT expose to the buyer.
      console.error("[auto-payout] failed", { orderId: data.orderId, error: payoutError });

      try {
        await supabaseAdmin.rpc("_log_payment_event" as never, {
          _order_id: data.orderId,
          _event_type: "automatic_payout_failed",
          _actor_id: userId,
          _amount_pence: null,
          _metadata: { error: payoutError, source: "buyer_confirmation" },
        } as never);

        // Notify admins so they can retry via the manual Release payout button.
        const { data: admins } = await supabaseAdmin
          .from("user_roles")
          .select("user_id")
          .eq("role", "admin");
        if (admins && admins.length > 0) {
          await supabaseAdmin.from("notifications").insert(
            admins.map((a) => ({
              user_id: a.user_id,
              type: "payout_released" as const, // reuse existing enum; message clarifies
              title: "Auto-payout failed — manual release needed",
              message: `Order ${data.orderId}: ${payoutError}`,
            })),
          );
        }
      } catch (logErr) {
        console.error("[auto-payout] failed to log/notify", logErr);
      }
    }

    return { ok: true, payoutAttempted, transferId: payoutTransferId };
  });
